import {
  BaseApiClient,
  BaseUser,
  BaseOrganization,
  type PaginationInfo,
  type PermissionLevel,
  type Project,
  type ProjectWithPermission,
  type Group,
  type GroupMember,
  type UserPermission,
  type GroupPermission,
  type ProjectUserPermission,
  type ProjectGroupPermission,
  type ExtractionRun,
  type ExtractionRunFile,
  type ExtractionSummary,
} from '../../shared/src';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.fakto.ai';

export interface User extends BaseUser {}

/** An automated import feeding the current project, with its most recent run (null = never ran). */
export interface ProjectExtraction extends ExtractionSummary {
  latest_run: ExtractionRun | null;
}

export interface Organization extends BaseOrganization {}

// Single-payload backings for the Permissions tab — each collapses a
// 1 + (1..2)N request fan-out into one call. The per-row maps are keyed by id
// (user/project/group), with an entry for every row (possibly empty).
export interface UsersPermissionsOverview {
  users: User[];
  groups: Group[];
  projects: Project[];
  userGroups: Record<string, Group[]>;
  userPermissions: Record<string, UserPermission[]>;
}

export interface ProjectsPermissionsOverview {
  projects: Project[];
  users: User[];
  groups: Group[];
  projectUserPermissions: Record<string, ProjectUserPermission[]>;
  projectGroupPermissions: Record<string, ProjectGroupPermission[]>;
}

export interface GroupsPermissionsOverview {
  groups: Group[];
  users: User[];
  projects: Project[];
  groupMembers: Record<string, GroupMember[]>;
  groupPermissions: Record<string, GroupPermission[]>;
}

export interface OrgInfo {
  slug: string;
  name: string;
  auth_method: 'sso' | 'password';
  status: string;
}

export interface FileUpload {
  id: string;
  file_name: string;
  original_name: string;
  file_size: number;
  content_type: string;
  s3_key: string;
  status: string;
  folder_id: string | null;
  description: string | null;
  created_at: string;
  completed_at: string | null;
  uploaded_by_email: string;
  uploaded_by_first_name: string;
  uploaded_by_last_name: string;
  /** Root-relative folder path ("Q4/Finance", "" for root); present only on project-scoped search results. */
  folder_path?: string | null;
}

// Admin > Audit Logs: the organization's own audit trail, as
// lambda/app-api/audit-allowlist.ts shapes it.
export type AuditCategory =
  | 'sign_in'
  | 'apps'
  | 'files'
  | 'folders'
  | 'people'
  | 'groups'
  | 'permissions'
  | 'projects'
  | 'exports';

export type AuditActor =
  | { type: 'user'; id: string; email: string; name: string | null }
  | { type: 'fakto_support' }
  | { type: 'deleted_user' };

export type AuditDetailValue = string | number | boolean | string[];

export interface AuditLogEntry {
  id: string;
  created_at: string;
  event: string;
  category: AuditCategory;
  actor: AuditActor;
  /** Null: the event belongs to no project. A null name: the project was deleted since. */
  project: { id: string; name: string | null } | null;
  details: Record<string, AuditDetailValue>;
  user_agent: string | null;
}

export type AuditLogSortColumn = 'time' | 'action' | 'user' | 'project';

/** `user_ids` value selecting Fakto Support's rows, `project_ids` value selecting rows of no project. */
export const FAKTO_SUPPORT_FILTER = 'fakto_support';
export const NO_PROJECT_FILTER = 'none';

/** Filters are ANDed; see lambda/app-api/audit-handlers.ts for their semantics. */
export interface AuditLogFilters {
  events?: string[];
  user_ids?: string[];
  project_ids?: string[];
  search?: string;
  exclude_fakto?: boolean;
  date_from?: string;
  date_to?: string;
  tz?: string;
}

export interface AuditLogFilterOptions {
  events: { event: string; category: AuditCategory }[];
  users: { id: string; email: string; name: string | null }[];
  projects: { id: string; name: string }[];
}

export type AuditExportFormat = 'csv' | 'xlsx';
export type AuditExportScope = 'all' | 'selection';

/** File-browser search scope: the current folder, or the whole project. */
export type UploadSearchScope = 'folder' | 'project';

export interface Folder {
  id: string;
  organization_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FolderWithMeta extends Folder {
  created_by_email: string;
  file_count: number;
  subfolder_count: number;
  /** Root-relative path of the folder's parent ("Q4/Finance", "" for root); present only on project-scoped search results. */
  folder_path?: string | null;
}

/**
 * Progress of the S3 re-key that follows a folder move/rename. The server
 * relocates objects in bounded chunks (one inline with the PATCH, then one per
 * POST /app/folders/:id/rekey call): `stale` is how many files still needed a
 * new key when the request ran, `moved` how many it relocated. Complete when
 * `stale - moved === 0` — `failed` files stay stale and are retried by the
 * next call, so a pass with failures is never complete. `interrupted` means
 * the PATCH committed but its inline chunk blew up before measuring anything:
 * progress is unknown and the caller must run the rekey loop to learn it.
 */
export interface FolderRekeyStatus {
  total: number;
  stale: number;
  moved: number;
  failed: number;
  interrupted?: boolean;
}

export interface FolderUpdateResult {
  folder: Folder;
  /** Present when the update changed the folder's path (move or rename). */
  rekey?: FolderRekeyStatus;
}

export interface GetUploadUrlParams {
  fileName: string;
  fileSize: number;
  contentType: string;
  fileHash: string;
  folderId?: string;
  description?: string;
}

export interface GetUploadUrlResponse {
  uploadId?: string;
  uploadUrl?: string;
  s3Key?: string;
  fileName?: string;
  renamed?: boolean;
  originalName?: string;
  expiresIn?: number;
  skipped?: boolean;
  existingFileName?: string;
  existingFolderPath?: string | null;
  message?: string;
}

// Batch upload (one round-trip for many files — see lambda/app-api/upload-handlers.ts)
export interface BatchUploadFileSpec {
  fileName: string;
  fileSize: number;
  contentType?: string;
  fileHash: string;
  folderId?: string;
  description?: string;
}

export interface BatchUploadUrlResult {
  index: number;
  uploadId?: string;
  uploadUrl?: string;
  s3Key?: string;
  fileName?: string;
  renamed?: boolean;
  originalName?: string;
  skipped?: boolean;
  existingFileName?: string;
  existingFolderPath?: string | null;
  error?: string;
  // Set alongside `error`: whether retrying the presign could succeed. Validation/filename
  // rejections are false; a transient (thrown) DB/S3 failure is true. Undefined for an older
  // backend, which the client treats as retryable.
  retryable?: boolean;
}

export interface CompleteUploadBatchResult {
  uploadId: string;
  status: 'completed' | 'failed';
  fileName?: string;
  message?: string;
}

export interface DeleteUploadBatchResult {
  uploadId: string;
  status: 'deleted' | 'failed' | 'forbidden';
  fileName?: string;
  message?: string;
}

class AppApiClient extends BaseApiClient {
  private isLoggingIn = false;

  constructor() {
    // '/auth/refresh' enables the sliding-session refresh: the client silently renews a
    // near-expiry access token (via the HttpOnly refresh cookie) so long uploads never die.
    super(API_URL, 'auth_token', '/auth/refresh');
  }

  protected onUnauthorized(): void {
    // Don't redirect during login attempts - let the error be shown
    if (this.isLoggingIn) {
      return;
    }
    // Expired/invalid session: send the user to sign-in. Routed through the reload gate,
    // so a 401 that lands mid-upload is deferred until the in-flight upload finishes.
    this.redirectToSignIn('session_expired');
  }

  // User
  async getMe(): Promise<User> {
    return this.request('GET', '/app/me');
  }

  async updateMe(data: { firstName?: string; lastName?: string }): Promise<User> {
    return this.request('PUT', '/app/me', data);
  }

  // Organization
  async getOrganization(): Promise<Organization> {
    return this.request('GET', '/app/organization');
  }

  // Org Info (public - for login page)
  async getOrgInfo(slug: string): Promise<OrgInfo> {
    return this.request('GET', `/auth/org-info?org=${slug}`);
  }

  // Password auth
  async passwordLogin(email: string, password: string, orgSlug: string): Promise<{ token: string; user: User }> {
    this.isLoggingIn = true;
    try {
      return await this.request('POST', '/auth/password/login', { email, password, org: orgSlug });
    } finally {
      this.isLoggingIn = false;
    }
  }

  async setPassword(token: string, password: string): Promise<{ token: string; user: User }> {
    return this.request('POST', '/auth/password/set-password', { token, password });
  }

  async requestPasswordReset(email: string, orgSlug: string): Promise<{ message: string }> {
    return this.request('POST', '/auth/password/request-reset', { email, org: orgSlug });
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.request('POST', '/auth/password/reset', { token, password });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request('POST', '/app/me/password', { currentPassword, newPassword });
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await this.request('POST', '/auth/logout');
    } catch {
      // Ignore errors on logout
    }
    // Clear local auth state (server clears HttpOnly cookies via Set-Cookie header)
    this.clearAuth();
  }

  // Login redirect (SSO)
  initiateLogin(): void {
    const org = window.location.hostname.split('.')[0];
    window.location.href = `${this.apiUrl}/auth/login?org=${org}`;
  }

  /**
   * Request a one-time handoff code to be redeemed at an external UI.
   *
   * Replaces the legacy `#token=<jwt>` redirect for `external_ui_url`-bearing
   * projects: webapp's `/auth/handoff` mints a 60-second single-use code,
   * the receiver redeems it server-to-server via `/auth/handoff/verify`,
   * mints its own audience-scoped session, sets a `__Host-` cookie, and
   * the receiver's SPA boots from that.
   *
   * The default handoff (VITE_EXTERNAL_UI_HANDOFF_MODE unset or "code"). A
   * failed mint does not fall back to the legacy token link — that would put
   * the full JWT in the URL of a host the server refused, or of a code-only
   * receiver. See App.tsx::handleSelectDashboardProject.
   */
  async requestHandoffCode(params: {
    projectSlug: string;
    externalHost: string;
  }): Promise<{ code: string; expires_in: number }> {
    return this.request('POST', '/auth/handoff', {
      project_slug: params.projectSlug,
      external_host: params.externalHost,
    });
  }

  // User Management (org_admin only)
  async listUsers(): Promise<User[]> {
    const response = await this.request<{ users: User[] }>('GET', '/app/users');
    return response.users;
  }

  async updateUserRole(userId: string, role: 'user' | 'org_admin'): Promise<{ user: User; message: string }> {
    return this.request('PUT', `/app/users/${userId}`, { role });
  }

  async inviteUser(data: {
    email: string;
    firstName: string;
    lastName: string;
    role: 'user' | 'org_admin';
  }): Promise<{ user: User; message: string }> {
    return this.request('POST', '/app/users', data);
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/app/users/${userId}`);
  }

  async resendInvitation(userId: string): Promise<{ message: string }> {
    return this.request('POST', `/app/users/${userId}/resend-invitation`, {});
  }

  // Upload Management (project-scoped, owner permission required)
  async getUploadUrl(params: GetUploadUrlParams & { projectId: string }): Promise<GetUploadUrlResponse> {
    return this.request('POST', '/app/uploads/get-url', params);
  }

  async completeUpload(uploadId: string): Promise<{ uploadId: string; status: string; message: string }> {
    return this.request('POST', '/app/uploads/complete', { uploadId });
  }

  // Batch presign: one request returns a presigned URL (or skip/rename) per file.
  async getUploadUrls(
    projectId: string,
    files: BatchUploadFileSpec[]
  ): Promise<BatchUploadUrlResult[]> {
    const res = await this.request<{ results: BatchUploadUrlResult[] }>(
      'POST',
      '/app/uploads/get-urls',
      { projectId, files }
    );
    return res.results;
  }

  // Batch confirm: one request verifies + finalizes many uploaded files.
  async completeUploadBatch(uploadIds: string[]): Promise<CompleteUploadBatchResult[]> {
    const res = await this.request<{ results: CompleteUploadBatchResult[] }>(
      'POST',
      '/app/uploads/complete-batch',
      { uploadIds }
    );
    return res.results;
  }

  async listUploads(
    projectId: string,
    folderId?: string,
    opts?: { page?: number; limit?: number; q?: string; scope?: UploadSearchScope },
  ): Promise<{ uploads: FileUpload[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams({ projectId });
    if (folderId) params.set('folderId', folderId);
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.q) params.set('q', opts.q);
    if (opts?.scope) params.set('scope', opts.scope);
    return this.request<{ uploads: FileUpload[]; pagination: PaginationInfo }>(
      'GET',
      `/app/uploads?${params}`,
    );
  }

  // Automated imports (read-only, project-scoped)
  async listProjectExtractions(projectId: string): Promise<{ extractions: ProjectExtraction[]; ledger_configured: boolean }> {
    return this.request('GET', `/app/projects/${projectId}/extractions`);
  }

  async listExtractionRuns(
    extractionKey: string,
    projectId: string,
    limit = 50,
  ): Promise<{ extraction: ExtractionSummary; runs: ExtractionRun[]; ledger_configured: boolean }> {
    const params = new URLSearchParams({ projectId, limit: String(limit) });
    return this.request('GET', `/app/extractions/${encodeURIComponent(extractionKey)}/runs?${params}`);
  }

  /** One page of a run's file rows; pass `next_cursor` back until it is null. */
  async getExtractionRun(
    extractionKey: string,
    runId: string,
    projectId: string,
    cursor?: string,
  ): Promise<{ extraction: ExtractionSummary; run: ExtractionRun; files: ExtractionRunFile[]; next_cursor: string | null }> {
    const params = new URLSearchParams({ projectId });
    if (cursor) params.set('cursor', cursor);
    return this.request('GET', `/app/extractions/${encodeURIComponent(extractionKey)}/runs/${encodeURIComponent(runId)}?${params}`);
  }

  async updateUpload(
    uploadId: string,
    data: { fileName?: string; description?: string; folderId?: string | null }
  ): Promise<FileUpload> {
    const response = await this.request<{ upload: FileUpload }>('PATCH', `/app/uploads/${uploadId}`, data);
    return response.upload;
  }

  async deleteUpload(uploadId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/app/uploads/${uploadId}`);
  }

  // Batch delete: one request removes many files (chunk to <=256 ids client-side).
  async deleteUploadsBatch(uploadIds: string[]): Promise<DeleteUploadBatchResult[]> {
    const res = await this.request<{ results: DeleteUploadBatchResult[] }>(
      'POST',
      '/app/uploads/delete-batch',
      { uploadIds }
    );
    return res.results;
  }

  // All completed file ids in a folder (for "Select all"). Capped server-side; `truncated`
  // is true when the folder holds more than the cap.
  async listUploadIds(
    projectId: string,
    folderId?: string,
    opts?: { q?: string; scope?: UploadSearchScope },
  ): Promise<{ ids: string[]; truncated: boolean }> {
    const params = new URLSearchParams({ projectId });
    if (folderId) params.set('folderId', folderId);
    if (opts?.q) params.set('q', opts.q);
    if (opts?.scope) params.set('scope', opts.scope);
    return this.request<{ ids: string[]; truncated: boolean }>('GET', `/app/uploads/ids?${params}`);
  }

  // Folder Management (project-scoped, owner permission required)
  async listFolders(
    projectId: string,
    parentId?: string,
    opts?: { q?: string; scope?: UploadSearchScope },
  ): Promise<{ folders: FolderWithMeta[]; truncated: boolean }> {
    const params = new URLSearchParams({ projectId });
    if (parentId) params.set('parentId', parentId);
    if (opts?.q) params.set('q', opts.q);
    if (opts?.scope) params.set('scope', opts.scope);
    const response = await this.request<{ folders: FolderWithMeta[]; truncated?: boolean }>(
      'GET',
      `/app/folders?${params}`,
    );
    return { folders: response.folders, truncated: response.truncated ?? false };
  }

  async getFolder(folderId: string): Promise<{ folder: Folder; path: Folder[] }> {
    return this.request('GET', `/app/folders/${folderId}`);
  }

  async createFolder(name: string, projectId: string, parentId?: string): Promise<Folder> {
    const response = await this.request<{ folder: Folder }>('POST', '/app/folders', {
      name,
      parentId: parentId || null,
      projectId,
    });
    return response.folder;
  }

  async updateFolder(
    folderId: string,
    data: { name?: string; description?: string | null; parentId?: string | null }
  ): Promise<FolderUpdateResult> {
    return this.request<FolderUpdateResult>('PATCH', `/app/folders/${folderId}`, data);
  }

  async moveFolder(folderId: string, targetParentId: string | null): Promise<FolderUpdateResult> {
    return this.updateFolder(folderId, { parentId: targetParentId });
  }

  async renameFolder(folderId: string, name: string): Promise<FolderUpdateResult> {
    return this.updateFolder(folderId, { name });
  }

  // One bounded chunk of the post-move/rename S3 relocation — see FolderRekeyStatus.
  async rekeyFolder(
    folderId: string
  ): Promise<{ done: boolean; total: number; stale: number; moved: number; failed: number }> {
    return this.request('POST', `/app/folders/${folderId}/rekey`, {});
  }

  async deleteFolder(folderId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/app/folders/${folderId}`);
  }

  // Group Management (org_admin only)
  async listGroups(): Promise<Group[]> {
    const response = await this.request<{ groups: Group[] }>('GET', '/app/groups');
    return response.groups;
  }

  async createGroup(name: string): Promise<Group> {
    const response = await this.request<{ group: Group }>('POST', '/app/groups', { name });
    return response.group;
  }

  async updateGroup(groupId: string, name: string): Promise<Group> {
    const response = await this.request<{ group: Group }>('PATCH', `/app/groups/${groupId}`, { name });
    return response.group;
  }

  async deleteGroup(groupId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/app/groups/${groupId}`);
  }

  async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('GET', `/app/groups/${groupId}/members`);
    return response.members;
  }

  async setGroupMembers(groupId: string, userIds: string[]): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('PUT', `/app/groups/${groupId}/members`, { userIds });
    return response.members;
  }

  // Per-member add/remove — preferred over setGroupMembers for UI toggles so
  // concurrent toggles can't clobber each other (no read-modify-write).
  async addGroupMember(groupId: string, userId: string): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('POST', `/app/groups/${groupId}/members/${userId}`, {});
    return response.members;
  }

  async removeGroupMember(groupId: string, userId: string): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('DELETE', `/app/groups/${groupId}/members/${userId}`);
    return response.members;
  }

  async getUserGroups(userId: string): Promise<Group[]> {
    const response = await this.request<{ groups: Group[] }>('GET', `/app/users/${userId}/groups`);
    return response.groups;
  }

  // Project Management (org_admin only)
  async listProjects(): Promise<Project[]> {
    const response = await this.request<{ projects: Project[] }>('GET', '/app/projects');
    return response.projects;
  }

  async createProject(name: string, slug: string): Promise<Project> {
    const response = await this.request<{ project: Project }>('POST', '/app/projects', { name, slug });
    return response.project;
  }

  async updateProject(projectId: string, name: string): Promise<Project> {
    const response = await this.request<{ project: Project }>('PATCH', `/app/projects/${projectId}`, { name });
    return response.project;
  }

  async deleteProject(projectId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/app/projects/${projectId}`);
  }

  // Permission Management
  async getMyProjects(minPermission?: PermissionLevel): Promise<ProjectWithPermission[]> {
    const query = minPermission ? `?minPermission=${minPermission}` : '';
    const response = await this.request<{ projects: ProjectWithPermission[] }>('GET', `/app/my-projects${query}`);
    return response.projects;
  }

  async getUserPermissions(userId: string): Promise<UserPermission[]> {
    const response = await this.request<{ permissions: UserPermission[] }>('GET', `/app/permissions/users/${userId}`);
    return response.permissions;
  }

  async setUserPermission(userId: string, projectId: string, permission: PermissionLevel | null): Promise<{ message: string }> {
    return this.request('PUT', `/app/permissions/users/${userId}/projects/${projectId}`, { permission });
  }

  async getGroupPermissions(groupId: string): Promise<GroupPermission[]> {
    const response = await this.request<{ permissions: GroupPermission[] }>('GET', `/app/permissions/groups/${groupId}`);
    return response.permissions;
  }

  async setGroupPermission(groupId: string, projectId: string, permission: PermissionLevel | null): Promise<{ message: string }> {
    return this.request('PUT', `/app/permissions/groups/${groupId}/projects/${projectId}`, { permission });
  }

  async getProjectPermissions(projectId: string): Promise<{ userPermissions: ProjectUserPermission[]; groupPermissions: ProjectGroupPermission[] }> {
    return this.request('GET', `/app/permissions/projects/${projectId}`);
  }

  // One round-trip each for the Permissions sub-tabs — replace the per-row
  // getUserGroups/getUserPermissions, getProjectPermissions, and
  // getGroupMembers/getGroupPermissions fan-outs on initial load.
  async getUsersPermissionsOverview(): Promise<UsersPermissionsOverview> {
    return this.request('GET', '/app/permissions/overview/users');
  }

  async getProjectsPermissionsOverview(): Promise<ProjectsPermissionsOverview> {
    return this.request('GET', '/app/permissions/overview/projects');
  }

  async getGroupsPermissionsOverview(): Promise<GroupsPermissionsOverview> {
    return this.request('GET', '/app/permissions/overview/groups');
  }

  // Audit Logs (org_admin only)
  async listAuditLogs(
    params: AuditLogFilters & { page?: number; limit?: number; sort_by?: AuditLogSortColumn; sort_order?: 'asc' | 'desc' }
  ): Promise<{ logs: AuditLogEntry[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '' || value === false) continue;
      if (Array.isArray(value)) {
        if (value.length > 0) query.set(key, value.join(','));
      } else {
        query.set(key, String(value));
      }
    }
    return this.request('GET', `/app/audit-logs?${query.toString()}`);
  }

  async getAuditLogFilters(): Promise<AuditLogFilterOptions> {
    return this.request('GET', '/app/audit-logs/filters');
  }

  /** Records the export (an audit row of its own) and returns what to fetch. */
  async createAuditLogExport(body: {
    format: AuditExportFormat;
    scope: AuditExportScope;
    filters?: AuditLogFilters;
  }): Promise<{ export: { id: string; total_rows: number; page_size: number } }> {
    return this.request('POST', '/app/audit-logs/exports', body);
  }

  /** One page of an export, oldest first; the last page ends with the export's own row. */
  async getAuditLogExportRows(
    exportId: string,
    cursor?: string
  ): Promise<{ rows: AuditLogEntry[]; next_cursor: string | null }> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.request('GET', `/app/audit-logs/exports/${exportId}/rows${query}`);
  }
}

export const api = new AppApiClient();

// Re-export types from shared for convenience
export type {
  PermissionLevel,
  Project,
  ProjectWithPermission,
  Group,
  GroupMember,
  UserPermission,
  GroupPermission,
  ProjectUserPermission,
  ProjectGroupPermission,
} from '../../shared/src';
